import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Home, ChevronRight, DollarSign, Save, Loader2, Trash2, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import WOAllocations from '@/components/WOAllocations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditWorksOrder = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [partDetails, setPartDetails] = useState(null);
  const [formData, setFormData] = useState({
    customer: '',
    customer_order_number: '',
    quantity: '',
    unit_price: '',
    due_date: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/works-orders/${orderNumber}`);
      setOrder(response.data);
      setFormData({
        customer: response.data.customer || '',
        customer_order_number: response.data.customer_order_number || '',
        quantity: response.data.quantity?.toString() || '',
        unit_price: response.data.unit_price?.toString() || '',
        due_date: response.data.due_date ? response.data.due_date.split('T')[0] : '',
        notes: response.data.notes || '',
        is_trial: response.data.is_trial || false
      });
      
      // Fetch part details
      if (response.data.part_number) {
        try {
          const partRes = await axios.get(`${BACKEND_URL}/api/parts/${response.data.part_number}`);
          setPartDetails(partRes.data);
        } catch (err) {
          console.error('Error fetching part details:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer || !formData.quantity || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const updateData = {
        customer: formData.customer,
        customer_order_number: formData.customer_order_number,
        quantity: parseInt(formData.quantity),
        unit_price: parseFloat(formData.unit_price) || 0,
        due_date: formData.due_date,
        notes: formData.notes,
        is_trial: formData.is_trial
      };
      
      await axios.put(`${BACKEND_URL}/api/works-orders/${orderNumber}`, updateData);
      toast.success('Works Order updated successfully');
      navigate('/schedule', { state: { searchOrder: orderNumber } });
    } catch (error) {
      console.error('Error updating works order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update works order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/works-orders/${orderNumber}`);
      toast.success('Works Order deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Error deleting works order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete works order');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const generateTraveller = () => {
    if (!order) return;
    const part = partDetails || {};
    const workbook = XLSX.utils.book_new();
    const rows = [];

    rows.push(['Hotforge (Pty) Ltd']);
    rows.push(['Works Order Traveller / Routing Sheet']);
    rows.push(['']);
    rows.push(['Works Order Number:', '', order.order_number]);
    rows.push(['Due Date:', '', order.due_date || formData.due_date || '']);
    rows.push(['']);
    rows.push(['ORDER DETAILS']);
    rows.push(['Field', '', 'Value']);
    rows.push(['Works Order Number', '', order.order_number]);
    rows.push(['Customer Name', '', order.customer || formData.customer || '']);
    rows.push(['Customer Order Number', '', order.customer_order_number || formData.customer_order_number || '']);
    rows.push(['Part Description', '', part.description || order.part_number || '']);
    rows.push(['Part Number', '', order.part_number || '']);
    rows.push(['Drawing Number', '', part.drawing_number || '']);
    rows.push(['Material', '', part.material_grade || '']);
    rows.push(['LOC / Length of Cut', '', part.loc ? `${part.loc} mm` : '']);
    rows.push(['Diameter', '', part.diameter ? `${part.diameter} mm` : '']);
    rows.push(['Quantity', '', order.quantity || formData.quantity || '']);
    rows.push(['Heat Treatment Specification', '', part.heat_treatment || '']);
    rows.push(['']);
    rows.push(['']);
    rows.push(['ROUTING / PROCESS SEQUENCE']);
    rows.push(['Process / Operation', 'Step', 'Planned Qty', 'Completed Qty', 'Scrap Qty', 'Operator Name', 'Date Completed']);

    const sequence = order.process_sequence || [];
    sequence.forEach((step, idx) => {
      rows.push([step, idx + 1, order.quantity || formData.quantity || '', '', '', '', '']);
    });
    for (let i = 0; i < 3; i++) rows.push(['', '', '', '', '', '', '']);

    rows.push(['']);
    rows.push(['']);
    rows.push(['Issued By:', '', user?.email || '', '', 'Date:', '', new Date().toLocaleDateString()]);
    rows.push(['']);
    rows.push(['ISO 9001:2015 Controlled Document']);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 24 }, { wch: 4 }, { wch: 28 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 18 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, ws, 'Traveller');
    XLSX.writeFile(workbook, `${order.order_number}_Traveller.xlsx`);
    toast.success(`Traveller downloaded: ${order.order_number}_Traveller.xlsx`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const orderValue = (parseInt(formData.quantity) || 0) * (parseFloat(formData.unit_price) || 0);

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-ewo" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(245, 158, 11, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(245, 158, 11, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-ewo)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(245, 158, 11, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-amber-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Save className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Edit Works Order</h1>
                <p className="text-xs text-slate-500 font-mono">{orderNumber}</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-amber-400 font-medium">Edit Order</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[900px] mx-auto">
          <form onSubmit={handleSubmit} className="bg-slate-900/40 backdrop-blur-md border border-amber-500/10 rounded-xl p-8" data-testid="edit-works-order-form">
            
            {/* Order Info Header */}
            <div className="mb-6 pb-6 border-b border-slate-800">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Order Number</p>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-mono font-bold text-amber-400">{order.order_number}</p>
                    {order.is_trial && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold uppercase tracking-widest rounded" data-testid="trial-badge">Trial</span>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteDialog(true)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Order
                </Button>
              </div>
            </div>

            {/* Part Details (Read Only) */}
            <div className="mb-6 bg-slate-900/60 border border-amber-500/20 rounded-lg p-4" data-testid="part-details">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Part Details (Read Only)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 text-xs">Part Number:</span>
                  <p className="font-mono text-white">{order.part_number}</p>
                </div>
                {partDetails && (
                  <>
                    <div>
                      <span className="text-slate-500 text-xs">Description:</span>
                      <p className="text-white">{partDetails.description}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Material:</span>
                      <p className="text-white">{partDetails.material_grade}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Drawing:</span>
                      <p className="font-mono text-white">{partDetails.drawing_number}</p>
                    </div>
                  </>
                )}
              </div>
              {order.process_sequence && (
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-xs text-slate-500 mb-2">Process Sequence:</p>
                  <div className="flex flex-wrap gap-2">
                    {order.process_sequence.map((proc, idx) => (
                      <span key={idx} className="px-2 py-1 bg-slate-800 text-white text-xs font-mono rounded border border-slate-700">
                        {idx + 1}. {proc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Customer *</Label>
                <Input
                  data-testid="customer-input"
                  value={formData.customer}
                  onChange={(e) => handleInputChange('customer', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="Customer name"
                  required
                />
              </div>

              {/* Customer Order Number */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Customer Order Number</Label>
                <Input
                  data-testid="customer-order-number-input"
                  value={formData.customer_order_number}
                  onChange={(e) => handleInputChange('customer_order_number', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="PO-12345"
                />
              </div>

              {/* Quantity */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Quantity *</Label>
                <Input
                  type="number"
                  data-testid="quantity-input"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="100"
                  min="1"
                  required
                />
              </div>

              {/* Unit Price */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Unit Price (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="unit-price-input"
                  value={formData.unit_price}
                  onChange={(e) => handleInputChange('unit_price', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="0.00"
                />
              </div>

              {/* Due Date */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Due Date *</Label>
                <Input
                  type="date"
                  data-testid="due-date-input"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  required
                />
              </div>

              {/* Order Value Display */}
              <div className="flex items-end">
                <div className="w-full p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Order Value</p>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                    <span className="text-2xl font-bold text-amber-400">
                      R {orderValue.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trial / First-Off */}
              <div className="md:col-span-2">
                <label
                  className={`flex items-center gap-3 cursor-pointer group p-4 rounded-lg border transition-colors ${
                    formData.is_trial ? 'border-red-500/50 bg-red-500/5' : 'border-slate-800 hover:border-red-500/40'
                  }`}
                  data-testid="trial-checkbox-label"
                >
                  <input
                    type="checkbox"
                    checked={formData.is_trial}
                    onChange={(e) => handleInputChange('is_trial', e.target.checked)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-950 text-red-500 focus:ring-red-500/50 cursor-pointer"
                    data-testid="trial-checkbox"
                  />
                  <div>
                    <span className="text-sm font-bold text-white uppercase tracking-wide group-hover:text-red-400 transition-colors">Trial / First-Off Order</span>
                    <p className="text-xs text-slate-500 mt-0.5">This order is flagged as a trial run</p>
                  </div>
                </label>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Notes</Label>
                <Textarea
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="bg-slate-950/50 border-slate-800 min-h-[100px]"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex gap-4 pt-6 border-t border-slate-800">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-btn"
                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/50 hover:shadow-[0_0_20px_rgba(245,158,11,0.3)] font-bold uppercase tracking-wide"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                onClick={generateTraveller}
                data-testid="print-traveller-btn"
                className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 font-bold uppercase tracking-wide"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Print Traveller
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                data-testid="cancel-btn"
                className="border-slate-700 hover:border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* Raw Material Allocations Section */}
          <div className="mt-6" data-testid="wo-allocations-wrapper">
            <WOAllocations orderNumber={orderNumber} user={user} />
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-red-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Works Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete order <span className="font-mono text-white">{orderNumber}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {deleting ? 'Deleting...' : 'Delete Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditWorksOrder;
